import React from "react";
import { ReactComponent as TrashIcon } from "../../assets/icons/trash.svg";
import styles from "./BusinessHoursEditor.module.css";

function cx(...classes) {
  return classes.filter(Boolean).join(" ");
}

// El backend usa enteros 0..6 para el día de la semana.
export const DAY_OPTIONS = [
  { value: 0, label: "Lunes" },
  { value: 1, label: "Martes" },
  { value: 2, label: "Miércoles" },
  { value: 3, label: "Jueves" },
  { value: 4, label: "Viernes" },
  { value: 5, label: "Sábado" },
  { value: 6, label: "Domingo" },
];

const DAY_LABEL_BY_VALUE = Object.fromEntries(
  DAY_OPTIONS.map((day) => [day.value, day.label]),
);

export function dayLabel(value) {
  return DAY_LABEL_BY_VALUE[Number(value)] ?? String(value);
}

// "09:00:00" | "09:00" -> "09:00" (formato que espera <input type="time">).
export function toTimeInputValue(time) {
  if (!time) return "";
  return String(time).slice(0, 5);
}

// Normaliza el array que viene del backend al formato que maneja el editor.
export function normalizeBusinessHours(rawHours) {
  if (!Array.isArray(rawHours)) return [];
  return rawHours
    .map((entry) => ({
      day_of_week: Number(entry.day_of_week),
      start_time: toTimeInputValue(entry.start_time),
      end_time: toTimeInputValue(entry.end_time),
    }))
    .sort((first, second) => first.day_of_week - second.day_of_week);
}

// Valida las reglas que el backend también verifica, para anticiparlas en cliente.
export function validateBusinessHours(rows) {
  const safeRows = Array.isArray(rows) ? rows : [];
  const rowErrors = safeRows.map(() => ({}));
  const firstIndexByDay = new Map();
  let message = "";

  safeRows.forEach((row, index) => {
    if (firstIndexByDay.has(row.day_of_week)) {
      rowErrors[index].day_of_week = "Día duplicado";
      rowErrors[firstIndexByDay.get(row.day_of_week)].day_of_week =
        "Día duplicado";
      message = "No puede haber más de un horario para el mismo día.";
    } else {
      firstIndexByDay.set(row.day_of_week, index);
    }

    if (!row.start_time || !row.end_time) {
      if (!row.start_time) rowErrors[index].start_time = "Requerido";
      if (!row.end_time) rowErrors[index].end_time = "Requerido";
      message = message || "Completá la hora de inicio y de fin de cada día.";
    } else if (row.end_time <= row.start_time) {
      rowErrors[index].end_time = "La hora de fin debe ser posterior";
      message =
        message || "La hora de fin debe ser posterior a la hora de inicio.";
    }
  });

  return { valid: !message, message, rowErrors };
}

const BusinessHoursEditor = ({
  value = [],
  onChange,
  errors,
  readOnly = false,
}) => {
  const rows = Array.isArray(value) ? value : [];
  const usedDays = new Set(rows.map((row) => row.day_of_week));
  const canAddRow = rows.length < DAY_OPTIONS.length;

  const handleAddRow = () => {
    const firstFreeDay = DAY_OPTIONS.find((day) => !usedDays.has(day.value));
    if (!firstFreeDay) return;
    onChange?.([
      ...rows,
      { day_of_week: firstFreeDay.value, start_time: "09:00", end_time: "18:00" },
    ]);
  };

  const handleRowChange = (index, field, fieldValue) => {
    const nextRows = rows.map((row, rowIndex) =>
      rowIndex === index ? { ...row, [field]: fieldValue } : row,
    );
    onChange?.(nextRows);
  };

  const handleRemoveRow = (index) => {
    onChange?.(rows.filter((_, rowIndex) => rowIndex !== index));
  };

  if (readOnly) {
    if (!rows.length) {
      return <div className={styles.empty}>Sin horarios configurados</div>;
    }
    const sortedRows = [...rows].sort(
      (first, second) => first.day_of_week - second.day_of_week,
    );
    return (
      <div className={styles.readOnlyList}>
        {sortedRows.map((row) => (
          <div key={row.day_of_week} className={styles.readOnlyRow}>
            <span className={styles.readOnlyDay}>{dayLabel(row.day_of_week)}</span>
            <span className={styles.readOnlyTime}>
              {toTimeInputValue(row.start_time) || "--:--"} –{" "}
              {toTimeInputValue(row.end_time) || "--:--"}
            </span>
          </div>
        ))}
      </div>
    );
  }

  return (
    <div className={styles.editor}>
      {rows.length > 0 && (
        <div className={cx(styles.grid, styles.headerRow)}>
          <span className={styles.headerCell}>Día</span>
          <span className={styles.headerCell}>Hora inicio</span>
          <span className={styles.headerCell}>Hora fin</span>
          <span className={styles.headerSpacer} />
        </div>
      )}

      {rows.map((row, index) => {
        const rowError = errors?.[index] ?? {};
        const dayOptions = DAY_OPTIONS.filter(
          (day) => day.value === row.day_of_week || !usedDays.has(day.value),
        );

        return (
          <div key={index} className={styles.grid}>
            <select
              className={cx(
                styles.daySelect,
                rowError.day_of_week && styles.fieldError,
              )}
              value={row.day_of_week}
              onChange={(event) =>
                handleRowChange(index, "day_of_week", Number(event.target.value))
              }
              title={rowError.day_of_week || undefined}
            >
              {dayOptions.map((day) => (
                <option key={day.value} value={day.value}>
                  {day.label}
                </option>
              ))}
            </select>

            <input
              type="time"
              className={cx(
                styles.timeInput,
                rowError.start_time && styles.fieldError,
              )}
              value={row.start_time}
              onChange={(event) =>
                handleRowChange(index, "start_time", event.target.value)
              }
              title={rowError.start_time || undefined}
            />

            <input
              type="time"
              className={cx(
                styles.timeInput,
                rowError.end_time && styles.fieldError,
              )}
              value={row.end_time}
              onChange={(event) =>
                handleRowChange(index, "end_time", event.target.value)
              }
              title={rowError.end_time || undefined}
            />

            <button
              type="button"
              className={styles.removeButton}
              onClick={() => handleRemoveRow(index)}
              aria-label="Eliminar horario"
            >
              <TrashIcon className={styles.removeIcon} />
            </button>
          </div>
        );
      })}

      {rows.length === 0 && (
        <div className={styles.empty}>Sin horarios configurados</div>
      )}

      {canAddRow && (
        <button type="button" className={styles.addButton} onClick={handleAddRow}>
          <span className={styles.addPlus}>+</span> Agregar día
        </button>
      )}
    </div>
  );
};

export default BusinessHoursEditor;
